// frontend/src/context/NotificationContext.js
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '../config';
import {
  isPushSupported,
  isPushSupportedAsync,
  isSubscribed as checkIsSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  requestPermission,
  getPermissionStatus,
  sendTestNotification
} from '../services/pushNotifications';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { currentUser, authFetch, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;

  const fetchNotifications = useCallback(async (unreadOnly = true, limit = 20) => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const response = await authFetchRef.current(
        `${API_URL}/api/notifications?unread=${unreadOnly}&limit=${limit}`
      );

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await authFetchRef.current(
        `${API_URL}/api/notifications/${notificationId}/read`,
        { method: 'POST' }
      );

      if (response.ok) {
        // Remove from list (we show unread only)
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const response = await authFetchRef.current(
        `${API_URL}/api/notifications/read-all`,
        { method: 'POST' }
      );

      if (response.ok) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  const clearRead = useCallback(async () => {
    try {
      const response = await authFetchRef.current(
        `${API_URL}/api/notifications/clear-read`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        // Remove read notifications from local state
        setNotifications(prev => prev.filter(n => !n.read));
      }
    } catch (error) {
      console.error('Error clearing read notifications:', error);
    }
  }, []);

  // Check push notification support and status
  const checkPushStatus = useCallback(async () => {
    // First do a quick sync check
    let supported = isPushSupported();

    // If not supported, try the async check which waits for service worker
    // This is more reliable on iOS where PushManager may only be available after SW registration
    if (!supported) {
      console.log('[NotificationContext] Sync check failed, trying async check...');
      supported = await isPushSupportedAsync();
    }

    setPushSupported(supported);
    console.log('[NotificationContext] Push supported:', supported);

    if (supported) {
      setPushPermission(getPermissionStatus());
      try {
        const subscribed = await checkIsSubscribed();
        setPushSubscribed(subscribed);
      } catch (err) {
        // Expected on localhost or when service worker isn't fully ready
        setPushSubscribed(false);
      }
    }
  }, []);

  // Subscribe to push notifications
  const subscribePush = useCallback(async () => {
    console.log('[NotificationContext] subscribePush called, token:', !!token, 'loading:', pushLoading);

    if (!token) {
      console.error('[NotificationContext] No auth token available');
      return { success: false, error: 'Not authenticated' };
    }

    if (pushLoading) {
      console.log('[NotificationContext] Already loading, skipping');
      return { success: false, error: 'Already in progress' };
    }

    setPushLoading(true);
    try {
      // First request permission if needed
      const permission = getPermissionStatus();
      console.log('[NotificationContext] Current permission:', permission);

      if (permission === 'default') {
        console.log('[NotificationContext] Requesting permission...');
        const permResult = await requestPermission();
        console.log('[NotificationContext] Permission result:', permResult);
        setPushPermission(permResult.permission);
        if (!permResult.success) {
          setPushLoading(false);
          return { success: false, error: 'Permission denied' };
        }
      } else if (permission === 'denied') {
        setPushLoading(false);
        return { success: false, error: 'Permission blocked. Please enable in browser settings.' };
      }

      console.log('[NotificationContext] Calling subscribeToPush...');
      const result = await subscribeToPush(token);
      console.log('[NotificationContext] subscribeToPush result:', result);

      if (result.success) {
        setPushSubscribed(true);
        console.log('[NotificationContext] Successfully subscribed!');
      } else {
        console.error('[NotificationContext] Subscription failed:', result.error);
      }
      return result;
    } catch (error) {
      console.error('[NotificationContext] Error subscribing to push:', error);
      return { success: false, error: error.message };
    } finally {
      setPushLoading(false);
    }
  }, [token, pushLoading]);

  // Unsubscribe from push notifications
  const unsubscribePush = useCallback(async () => {
    if (!token || pushLoading) return { success: false };

    setPushLoading(true);
    try {
      const result = await unsubscribeFromPush(token);
      if (result.success) {
        setPushSubscribed(false);
      }
      return result;
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      return { success: false, error: error.message };
    } finally {
      setPushLoading(false);
    }
  }, [token, pushLoading]);

  // Send test push notification
  const testPush = useCallback(async () => {
    if (!token) return { success: false, error: 'Not authenticated' };
    return sendTestNotification(token);
  }, [token]);

  // Fetch notifications on component mount and when user changes
  const checkPushStatusRef = useRef(checkPushStatus);
  checkPushStatusRef.current = checkPushStatus;
  const fetchNotificationsRef = useRef(fetchNotifications);
  fetchNotificationsRef.current = fetchNotifications;

  useEffect(() => {
    if (currentUser) {
      fetchNotificationsRef.current();
      checkPushStatusRef.current();

      // Set up polling to check for new notifications
      const interval = setInterval(() => {
        fetchNotificationsRef.current();
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const value = useMemo(() => ({
    // Existing notification values
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearRead,
    // Push notification values
    pushSupported,
    pushPermission,
    pushSubscribed,
    pushLoading,
    subscribePush,
    unsubscribePush,
    testPush,
    checkPushStatus,
  }), [notifications, unreadCount, loading, fetchNotifications, markAsRead,
    markAllAsRead, clearRead, pushSupported, pushPermission, pushSubscribed,
    pushLoading, subscribePush, unsubscribePush, testPush, checkPushStatus]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
